/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {TableModel} from '../../../shared/table/models/table.model';
import {SettlementAgreementColumnEnum} from './models/settlement-agreement-column.enum';
import settlementAgreementTableHeaders from './settlement-agreement-table-headers-configuration';
import {TableHeaderEntryModel} from '../../../shared/table/models/table-header-entry.model';
import {TableLineDataModel} from '../../../shared/table/models/table-line-data.model';
import {SettlementDetailAgreementTableDataModel} from '../../models/settlement-detail-agreement-table-data.model';
import {TableLineModel} from '../../../shared/table/models/table-line.model';
import {Clipboard} from '@angular/cdk/clipboard';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {FlyingMenuComponent} from '../../../shared/flying-menu/flying-menu.component';

@Component({
    selector: 'app-settlement-agreement-table',
    templateUrl: './settlement-agreement-table.component.html',
    styleUrls: ['./settlement-agreement-table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class SettlementAgreementTableComponent implements AfterViewInit {
    @Input() settlementTableData: SettlementDetailAgreementTableDataModel[];
    @ViewChild('tableCopyBlueFapiaoTemplate') tableCopyBlueFapiaoTemplate: TemplateRef<ElementRef>;
    @ViewChild('tableCopyDeliveryNumberTemplate') tableCopyDeliveryNumberTemplate: TemplateRef<ElementRef>;
    readonly TOOLTIP_BLUE_FAPIAO_COPY = 'Copy blue fapiao number to clipboard';
    readonly TOOLTIP_DELIVERY_NOTE_COPY = 'Copy delivery number to clipboard ';
    readonly UNDEFINED_PIPE = '-';
    SettlementAgreementColumnEnum = SettlementAgreementColumnEnum;
    @ViewChild('commentTableTemplate') commentTableTemplate: TemplateRef<FlyingMenuComponent>;
    readonly LABEL_COMMENT_FLYING_MENU = 'Comment';
    readonly TOOLTIP_COMMENT = 'View comment';
    readonly TOOLTIP_NO_COMMENT = 'No comment available';

    public tableData: TableModel<SettlementAgreementColumnEnum, TableLineDataModel> = {
        labels: {
            labelsData: ['Part(s) block', 'TE minutes block', 'Identity block'],
            hasInnerBoldBorder: true
        },
        headers: {
            entries: new Map<SettlementAgreementColumnEnum, TableHeaderEntryModel<SettlementAgreementColumnEnum>>([
                [SettlementAgreementColumnEnum.QUANTITY, settlementAgreementTableHeaders.quantity],
                [SettlementAgreementColumnEnum.MATERIALS_UNIT_NET_PRICE, settlementAgreementTableHeaders.materialsUnitNetPrice],
                [SettlementAgreementColumnEnum.MATERIALS_TOTAL_NET_COST, settlementAgreementTableHeaders.materialsTotalNetCost],
                [SettlementAgreementColumnEnum.TAX_RATE, settlementAgreementTableHeaders.taxRate],
                [SettlementAgreementColumnEnum.MATERIALS_TOTAL_GROSS_COST, settlementAgreementTableHeaders.materialsTotalGrossCost],
                [SettlementAgreementColumnEnum.TE_AMOUNT, settlementAgreementTableHeaders.teMinutesAmount],
                [SettlementAgreementColumnEnum.TE_UNIT_COST, settlementAgreementTableHeaders.teMinutesUnitCost],
                [SettlementAgreementColumnEnum.TE_ADDITIONAL_COSTS, settlementAgreementTableHeaders.teMinutesAdditionalCosts],
                [SettlementAgreementColumnEnum.TE_TOTAL_COST, settlementAgreementTableHeaders.teMinutesTotalCost],
                [SettlementAgreementColumnEnum.BLUE_FAPIAO_NUMBER, settlementAgreementTableHeaders.blueFapiaoNumber],
                [SettlementAgreementColumnEnum.DELIVERY_NUMBER, settlementAgreementTableHeaders.deliveryNumber],
                [SettlementAgreementColumnEnum.COMMENTS, settlementAgreementTableHeaders.comments]
            ])
        },
        expandableLines: false
    };

    constructor(private cdr: ChangeDetectorRef,
                private clipboard: Clipboard,
                private toast: ToastWrapperService) {
    }

    ngAfterViewInit(): void {
        this.tableData.staticSection = this.tableDataLines(this.settlementTableData);
        this.cdr.detectChanges();
    }

    copySettlementTableClick(copyType: SettlementAgreementColumnEnum, copyValue: string): void {
        if (copyValue != null) {
            this.clipboard.copy(copyValue);
            if (copyType === SettlementAgreementColumnEnum.BLUE_FAPIAO_NUMBER) {
                this.toast.success(`The Blue fapiao ${copyValue} was successfully copied to clipboard.`, 'Success');
            } else {
                this.toast.success(`The Delivery note ${copyValue} was successfully copied to clipboard.`, 'Success');
            }
        }
    }

    tooltipCommentText(comment: string): string {
        return comment != null ? this.TOOLTIP_COMMENT : this.TOOLTIP_NO_COMMENT;
    }

    private tableDataLines(value: SettlementDetailAgreementTableDataModel[]): any {
        let lines = [];
        lines = value?.map((item) => ({
            data: this.mapLineData(item)
        } as TableLineModel<SettlementAgreementColumnEnum, SettlementDetailAgreementTableDataModel>));

        return lines;
    }

    private mapLineData(line: SettlementDetailAgreementTableDataModel): Map<SettlementAgreementColumnEnum, TableLineDataModel> {
        const map: Map<SettlementAgreementColumnEnum, TableLineDataModel> = new Map<SettlementAgreementColumnEnum, TableLineDataModel>([
            [SettlementAgreementColumnEnum.QUANTITY, {
                cellValue: line?.quantity
            }],
            [SettlementAgreementColumnEnum.MATERIALS_UNIT_NET_PRICE, {
                cellValue: {
                    data: line?.materialsUnitNetPrice,
                    currency: line?.currency
                }
            }],
            [SettlementAgreementColumnEnum.MATERIALS_TOTAL_NET_COST, {
                cellValue: {
                    data: line?.materialsTotalNetCost,
                    currency: line?.currency
                }
            }],
            [SettlementAgreementColumnEnum.TAX_RATE, {
                cellValue: line?.taxRate
            }],
            [SettlementAgreementColumnEnum.MATERIALS_TOTAL_GROSS_COST, {
                cellValue: {
                    data: line?.materialsTotalGrossCost,
                    currency: line?.currency
                }
            }],
            [SettlementAgreementColumnEnum.TE_AMOUNT, {
                cellValue: line?.teMinutesAmount
            }],
            [SettlementAgreementColumnEnum.TE_UNIT_COST, {
                cellValue: {
                    data: line?.teMinutesUnitCost,
                    currency: line?.currency
                }
            }],
            [SettlementAgreementColumnEnum.TE_ADDITIONAL_COSTS, {
                cellValue: {
                    data: line?.teMinutesAdditionalCosts,
                    currency: line?.currency
                }
            }],
            [SettlementAgreementColumnEnum.TE_TOTAL_COST, {
                cellValue: {
                    data: line?.teMinutesTotalCost,
                    currency: line?.currency
                }
            }],
            [SettlementAgreementColumnEnum.BLUE_FAPIAO_NUMBER, {
                cellValue: {
                    templateRef: this.tableCopyBlueFapiaoTemplate,
                    context: {
                        blueFapiaoNumber: line.blueFapiaoNumber
                    }
                }
            }],
            [SettlementAgreementColumnEnum.DELIVERY_NUMBER, {
                cellValue: {
                    templateRef: this.tableCopyDeliveryNumberTemplate,
                    context: {
                        deliveryNumber: line.deliveryNumber
                    }
                }
            }],
            [SettlementAgreementColumnEnum.COMMENTS, {
                cellValue: {
                    templateRef: this.commentTableTemplate,
                    context: {
                        commentTooltip: this.tooltipCommentText(line.comments),
                        commentValue: line.comments,
                        commentIsNull: line.comments == null
                    }
                }
            }]
        ]);
        return map;
    }
}
